import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledChip from "components/shared/StyledChip";
import { SmallBody } from "helpers/TypographyComponents";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { addCompanyPeriodType } from "store/adminPanelSlice";
import { fetchPeriodTypes, PeriodType } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";

const ALL_DURATIONS = [1, 3, 6, 12];

const row = {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap" as const,
};

export const AddPeriodType = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { periodTypes } = useAppSelector((state) => state.period);

    const [duration, setDuration] = useState<number>(3);
    const [periodStart, setPeriodStart] = useState<Date | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        dispatch(fetchPeriodTypes());
    }, [dispatch]);

    const durationLabel: Record<number, string> = {
        1: t("AdminPanel.PeriodDurationMonthly"),
        3: t("AdminPanel.PeriodDurationQuarterly"),
        6: t("AdminPanel.PeriodDurationSemesterly"),
        12: t("AdminPanel.PeriodDurationYearly"),
    };

    const availableDurations = ALL_DURATIONS.filter(
        (d) => !periodTypes.some((pt: PeriodType) => Number(pt.duration) === d),
    );

    const handleSubmit = async () => {
        if (!periodStart) return;
        setIsSubmitting(true);
        try {
            const isoDate = [
                periodStart.getFullYear(),
                String(periodStart.getMonth() + 1).padStart(2, "0"),
                String(periodStart.getDate()).padStart(2, "0"),
            ].join("-");
            await dispatch(addCompanyPeriodType({ duration, periodStart: isoDate })).unwrap();
            await dispatch(fetchPeriodTypes());
            toast.success(t("AdminPanel.PeriodTypeAddSuccess") as string);
            setPeriodStart(null);
        } catch {
            toast.error(t("AdminPanel.PeriodTypeAddError") as string);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {periodTypes.length > 0 && (
                <div style={{ ...row, marginBottom: "8px" }}>
                    <SmallBody color="grey">{t("AdminPanel.PeriodTypesAlreadyConfigured")}</SmallBody>
                    {periodTypes.map((pt: PeriodType) => (
                        <StyledChip key={pt.id} label={durationLabel[Number(pt.duration)] ?? `${pt.duration} mois`} />
                    ))}
                </div>
            )}

            {availableDurations.length > 0 && (
                <div style={{ ...row, marginTop: "8px" }}>
                    <StyledSelect
                        value={duration}
                        onChange={(e) => setDuration(Number(e.target.value))}
                        size="small"
                        sx={{ minWidth: 180 }}
                    >
                        {availableDurations.map((d) => (
                            <StyledMenuItem key={d} value={d}>
                                {durationLabel[d]}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>

                    <StyledDatePicker
                        value={periodStart}
                        onChange={(date: Date | null) => setPeriodStart(date)}
                        maxYearDistance={10}
                        inputFormat="dd/MM/yyyy"
                        textFieldSx={{ width: 160 }}
                    />

                    <StyledButton buttonType="primary" onClick={handleSubmit} disabled={isSubmitting || !periodStart}>
                        {t("AdminPanel.Add")}
                    </StyledButton>
                </div>
            )}
        </>
    );
};
